
"use client";

import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

interface Links {
  label: string;
  href: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
  width: {
    collapsed: number;
    expanded: number;
  };
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useAnimatedSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useAnimatedSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const AnimatedSidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  // Define standard widths for the sidebar (as numbers for easier calculations)
  const width = {
    collapsed: 60,
    expanded: 300
  };

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate, width }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const AnimatedSidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <AnimatedSidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </AnimatedSidebarProvider>
  );
};

type AnimatedSidebarBodyProps = {
  className?: string;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof motion.div>, 'children' | 'className'>;

export const AnimatedSidebarBody = (props: AnimatedSidebarBodyProps) => {
  const { className, children, ...motionProps } = props;
  const location = useLocation();
  const isBuilderPage = location.pathname.includes('/builder');
  
  return (
    <>
      <DesktopSidebar 
        className={className} 
        {...motionProps}
        isBuilderPage={isBuilderPage}
      >
        {children}
      </DesktopSidebar>
      <MobileSidebar className={className}>
        {children}
      </MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  isBuilderPage = false,
  ...props
}: React.ComponentProps<typeof motion.div> & {
  children: React.ReactNode;
  className?: string;
  isBuilderPage?: boolean;
}) => {
  const { open, setOpen, animate, width } = useAnimatedSidebar();
  
  return (
    <motion.div
      className={cn(
        "h-full px-4 py-4 hidden md:flex md:flex-col bg-gray-900 border-r border-gray-800 z-40",
        "left-0 top-0",
        className
      )}
      animate={{
        width: animate ? (open ? width.expanded : width.collapsed) : width.expanded,
      }}
      style={{
        height: "100%", 
        position: "fixed",
        overflowY: "auto",
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  const { open, setOpen } = useAnimatedSidebar();
  return (
    <>
      <div className="fixed top-16 left-0 z-50 md:hidden">
        <div className="flex justify-end z-50 w-full">
          <Menu
            className="text-gray-300 cursor-pointer m-3"
            onClick={() => setOpen(!open)}
          />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={cn(
                "fixed h-full w-full inset-0 bg-gray-900 p-10 z-[100] flex flex-col justify-between",
                className
              )}
            >
              <div
                className="absolute right-10 top-10 z-50 text-gray-300 cursor-pointer"
                onClick={() => setOpen(!open)}
              >
                <X />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export const AnimatedSidebarLink = ({
  link,
  className,
  ...props
}: {
  link: Links;
  className?: string;
  props?: React.ComponentProps<"div">;
}) => {
  const { open, animate } = useAnimatedSidebar();
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (link.onClick) {
      link.onClick();
    } else if (link.href && link.href !== '#') {
      navigate(link.href);
    }
  };
  
  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-center justify-start gap-2 group/sidebar py-2 cursor-pointer",
        className
      )}
      {...props}
    >
      {link.icon}
      <motion.span
        animate={{
          display: animate ? (open ? "inline-block" : "none") : "inline-block",
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-gray-300 text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
      >
        {link.label}
      </motion.span>
    </div>
  );
};
